import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardDoubleArrowLeft from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import {
  Box,
  FormHelperText,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const GRID_PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 150, 200];

const TEXT_COLOR = "#1F2433";

// Global styles give every .MuiInputBase-root `padding: 8px 12px` and a dark text
// colour; the doubled class wins over them so the 56 x 40 input and the select
// spacing match the design.
const INPUT_BASE_ROOT_RESET = {
  "&.MuiInputBase-root.MuiInputBase-root": { padding: 0, color: TEXT_COLOR },
};

export interface GridPaginationProps {
  /** Zero-based current page. */
  page: number;
  pageSize: number;
  rowCount: number;
  pageSizeOptions?: number[];
  /** Receives a zero-based page index. */
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const formatNumber = (value: number) => value.toLocaleString("en-US");

const Separator = () => (
  <Box component="span" aria-hidden sx={{ mx: 1.5, fontSize: 8, opacity: 0.6 }}>
    •
  </Box>
);

const GridPagination = ({
  page,
  pageSize,
  rowCount,
  pageSizeOptions = GRID_PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
}: GridPaginationProps): JSX.Element => {
  const { t } = useTranslation();

  const pageCount = Math.max(1, Math.ceil(rowCount / pageSize));
  const currentPage = Math.min(Math.max(page, 0), pageCount - 1);
  const from = rowCount === 0 ? 0 : currentPage * pageSize + 1;
  const to = Math.min(rowCount, (currentPage + 1) * pageSize);
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === pageCount - 1;

  const [pageText, setPageText] = useState(String(currentPage + 1));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setPageText(String(currentPage + 1));
    setInvalid(false);
  }, [currentPage]);

  const sizeOptions = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  const goToTypedPage = () => {
    const text = pageText.trim();
    if (/^\d+$/.test(text)) {
      const target = Number(text);
      if (target >= 1 && target <= pageCount) {
        setInvalid(false);
        setPageText(String(target));
        onPageChange(target - 1);
        return;
      }
    }
    setInvalid(true);
  };

  const resetPageText = () => {
    setPageText(String(currentPage + 1));
    setInvalid(false);
  };

  const navButton = (
    label: string,
    icon: React.ReactNode,
    disabled: boolean,
    target: number
  ) => (
    <Tooltip title={label}>
      {/* span keeps the tooltip working while the button is disabled */}
      <span>
        <IconButton
          size="small"
          color="inherit"
          aria-label={label}
          disabled={disabled}
          onClick={() => onPageChange(target)}
          sx={{ "&:not(.Mui-disabled)": { opacity: 0.6 } }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box
      className="grid-pagination"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        // stay on the right even when the footer container centres its children
        ml: "auto",
        flexWrap: "wrap",
        rowGap: 1,
        // 8px between the page field and the footer line. A 40px field in
        // this bar is as tall as the footer, so the box sits on the border
        // and the fixed-height grid (Add Users is 364px) jumps, then clips.
        py: 1,
        px: 2,
        fontSize: 14,
        color: TEXT_COLOR,
      }}
    >
      <Box component="span" id="grid-pagination-rows-label" sx={{ mr: 1 }}>
        {t("common.pagination.rowsPerPage")}
      </Box>
      <Select
        variant="standard"
        disableUnderline
        value={pageSize}
        labelId="grid-pagination-rows-label"
        onChange={(event) => onPageSizeChange(Number(event.target.value))}
        IconComponent={KeyboardArrowDown}
        sx={{
          ...INPUT_BASE_ROOT_RESET,
          fontSize: 14,
          // 20px arrow + 4px gap to the number; no focus background
          "& .MuiSelect-select.MuiSelect-select": {
            height: "auto",
            minHeight: 0,
            padding: "0 24px 0 0",
            lineHeight: "20px",
            backgroundColor: "transparent",
          },
          "& .MuiSelect-select.MuiSelect-select:focus": {
            backgroundColor: "transparent",
          },
          "& .MuiSelect-icon": { color: TEXT_COLOR, fontSize: 20, right: 0, opacity: 0.6 },
        }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
      >
        {sizeOptions.map((size) => (
          <MenuItem key={size} value={size} sx={{ fontSize: 14, color: TEXT_COLOR }}>
            {size}
          </MenuItem>
        ))}
      </Select>

      <Separator />
      <span>
        {t("common.pagination.range", {
          from: formatNumber(from),
          to: formatNumber(to),
          total: formatNumber(rowCount),
        })}
      </span>
      <Separator />

      {navButton(
        t("common.pagination.firstPage"),
        <KeyboardDoubleArrowLeft fontSize="small" />,
        isFirstPage,
        0
      )}
      {navButton(
        t("common.pagination.previousPage"),
        <ChevronLeft fontSize="small" />,
        isFirstPage,
        currentPage - 1
      )}

      <Box component="span" sx={{ mx: 1.5 }}>
        {t("common.pagination.page")}
      </Box>
      <Box sx={{ position: "relative" }}>
        {/* InputBase on purpose: the global form styles are scoped to
            .MuiFormControl-root / .MuiOutlinedInput-root and would break this
            box. 32px keeps the field inside the footer; 4px padding keeps the
            digit off the border. The extra .MuiInputBase-root beats
            `.react-table .MuiInputBase-input` (height 28px, padding 4px 12px),
            which otherwise applies after first paint and snaps the field shut. */}
        <InputBase
          value={pageText}
          error={invalid}
          onChange={(event) => {
            setPageText(event.target.value);
            setInvalid(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              goToTypedPage();
            }
          }}
          onBlur={resetPageText}
          inputProps={{
            "aria-label": t("common.pagination.pageInput"),
            "aria-describedby": invalid ? "grid-pagination-error" : undefined,
            inputMode: "numeric",
          }}
          sx={{
            width: 56,
            alignSelf: "center",
            flexShrink: 0,
            boxSizing: "border-box",
            border: "2px solid",
            borderColor: invalid ? "error.main" : "rgba(0, 0, 0, 0.23)",
            borderRadius: "4px",
            fontSize: 14,
            "&.Mui-focused": {
              borderColor: invalid ? "error.main" : "primary.main",
            },
            "&.MuiInputBase-root.MuiInputBase-root": {
              height: 32,
              padding: "4px 8px",
              color: TEXT_COLOR,
              boxSizing: "border-box",
            },
            "&.MuiInputBase-root .MuiInputBase-input.MuiInputBase-input": {
              boxSizing: "border-box",
              height: 20,
              minHeight: 0,
              padding: 0,
              lineHeight: "20px",
              textAlign: "left",
              backgroundColor: "transparent",
            },
          }}
        />
        {invalid && (
          <FormHelperText
            error
            id="grid-pagination-error"
            role="alert"
            sx={{
              position: "absolute",
              top: "100%",
              right: 0,
              m: 0,
              whiteSpace: "nowrap",
            }}
          >
            {t("common.pagination.invalidPage", {
              total: formatNumber(pageCount),
            })}
          </FormHelperText>
        )}
      </Box>
      <Box component="span" sx={{ mx: 1.5 }}>
        {t("common.pagination.ofPages", { total: formatNumber(pageCount) })}
      </Box>

      {navButton(
        t("common.pagination.nextPage"),
        <ChevronRight fontSize="small" />,
        isLastPage,
        currentPage + 1
      )}
      {navButton(
        t("common.pagination.lastPage"),
        <KeyboardDoubleArrowRight fontSize="small" />,
        isLastPage,
        pageCount - 1
      )}
    </Box>
  );
};

export default GridPagination;
